// Composable UI building blocks shared across the analytics views. All plain
// DOM, styled by the tokens in styles.css.

import { el } from './dom';
import { sparkline } from './charts';

export type Tone = 'default' | 'ok' | 'warn' | 'danger' | 'flag';

export interface KpiOptions {
  /** Small caption under the value, e.g. "+3 this window". */
  sub?: string;
  /** A second muted caption line, e.g. a per-currency breakdown. */
  sub2?: string;
  tone?: Tone;
  /** Trend sparkline values (drawn small, top-right). */
  spark?: number[];
  sparkColor?: string;
  title?: string;
}

/** A single stat card. `value` is pre-formatted. */
export function kpiCard(
  label: string,
  value: string,
  opts: KpiOptions = {},
): HTMLElement {
  const tone = opts.tone ?? 'default';
  const cls = ['card', 'kpi', tone !== 'default' ? `kpi-${tone}` : '']
    .filter(Boolean)
    .join(' ');
  return el('div', { class: cls, title: opts.title ?? '' }, [
    opts.spark && opts.spark.length > 1
      ? el('div', { class: 'kpi-spark' }, [
          sparkline(opts.spark, opts.sparkColor) as unknown as Node,
        ])
      : null,
    el('div', { class: 'card-value' }, [value]),
    el('div', { class: 'card-label' }, [label]),
    opts.sub ? el('div', { class: 'card-sub' }, [opts.sub]) : null,
    opts.sub2 ? el('div', { class: 'card-sub' }, [opts.sub2]) : null,
  ]);
}

export function kpiGrid(cards: (HTMLElement | null | false)[]): HTMLElement {
  return el(
    'div',
    { class: 'cards' },
    cards.filter((c): c is HTMLElement => !!c),
  );
}

export interface PanelOptions {
  subtitle?: string;
  actions?: HTMLElement[];
  /** Let the panel span the full grid width. */
  wide?: boolean;
}

/** A titled surface for a chart or breakdown. */
export function panel(
  title: string,
  body: (HTMLElement | SVGElement | null | false)[],
  opts: PanelOptions = {},
): HTMLElement {
  const head = el('div', { class: 'panel-head' }, [
    el('div', {}, [
      el('h2', { class: 'panel-title' }, [title]),
      opts.subtitle
        ? el('p', { class: 'panel-sub' }, [opts.subtitle])
        : null,
    ]),
    opts.actions && opts.actions.length
      ? el('div', { class: 'panel-actions' }, opts.actions)
      : null,
  ]);
  return el('section', { class: `panel${opts.wide ? ' panel-wide' : ''}` }, [
    head,
    el(
      'div',
      { class: 'panel-body' },
      body.filter((b): b is HTMLElement => !!b) as unknown as Node[],
    ),
  ]);
}

/** A grid that lays panels out responsively (min 320px columns). */
export function panelGrid(panels: (HTMLElement | null | false)[]): HTMLElement {
  return el(
    'div',
    { class: 'panel-grid' },
    panels.filter((p): p is HTMLElement => !!p),
  );
}

export interface SegmentedOption {
  label: string;
  value: string;
}

/**
 * A controlled segmented control (pill group). The caller owns state: pass the
 * current `selected` value and an `onChange` that re-renders.
 */
export function segmented(
  options: SegmentedOption[],
  selected: string,
  onChange: (value: string) => void,
): HTMLElement {
  return el(
    'div',
    { class: 'segmented', role: 'tablist' },
    options.map((o) =>
      el(
        'button',
        {
          class: `seg${o.value === selected ? ' seg-on' : ''}`,
          role: 'tab',
          onClick: () => {
            if (o.value !== selected) onChange(o.value);
          },
        },
        [o.label],
      ),
    ),
  );
}

/** A muted "as of / caption" line. */
export function caption(text: string): HTMLElement {
  return el('p', { class: 'view-caption' }, [text]);
}

/** Section heading with optional right-aligned controls. */
export function sectionHead(
  title: string,
  right?: HTMLElement,
): HTMLElement {
  return el('div', { class: 'section-head' }, [
    el('h2', {}, [title]),
    right ?? null,
  ]);
}
